import { memo } from 'react'
import { IconButton, styled, Typography } from '@mui/material'
import { CloseIcon } from '$common/icons/close'
import { RemoveIcon } from '$common/icons/remove'
import { toLocale } from '$func/text'
import { ForwardIcon } from '$common/icons/forward'
import { CopyIcon } from '$common/icons/copy'
import { EditOutlinedIcon } from '$common/icons'
import { exitRoomFromSelectionMode } from '$logic/router'
import { ROOM_APP_BAR_HEIGHT } from '$common/room-app-bar/conf'

const Container = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutral000,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 7px',
  height: `${ROOM_APP_BAR_HEIGHT}px`,
  flexShrink: '0',
  borderBottom: '1px solid',
  borderColor: theme.palette.neutral300,
}))

const RowDirectionStack = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 100%;
`

const StyledIconButton = styled(IconButton)<{ 'data-is-disable'?: boolean }>`
  padding: 12px;
  height: 40px;
  width: 40px;
  &[data-is-disable='true'] {
    pointer-events: none;
  }
`

const StyledCloseIconButton = styled(IconButton)`
  padding: 12px;
  /* @noflip */
  margin-right: 18px;
`

interface Props {
  selectionCount: number
  canDelete: boolean
  canEdit: boolean
  canCopy: boolean
  canForward: boolean
  onForward: () => unknown
  onEdit: () => unknown
  onDelete: () => unknown
  onCopy: () => unknown
  onBack?: () => unknown
  arePermissionsStale?: boolean
}
export const RoomAppBarSelection = memo(function RoomAppBarSelectionComponent({
  selectionCount,
  canEdit,
  canCopy,
  canDelete,
  canForward,
  onForward,
  onEdit,
  onDelete,
  onCopy,
  onBack,
  arePermissionsStale,
}: Props) {
  return (
    <Container dir="rtl">
      <RowDirectionStack>
        {canForward && (
          <StyledIconButton
            onClick={onForward}
            data-is-disable={arePermissionsStale}
          >
            <ForwardIcon sx={{ color: 'neutral900' }} />
          </StyledIconButton>
        )}

        {canCopy && (
          <StyledIconButton
            onClick={onCopy}
            data-is-disable={arePermissionsStale}
          >
            <CopyIcon sx={{ color: 'neutral900' }} />
          </StyledIconButton>
        )}

        {canDelete && (
          <StyledIconButton
            onClick={onDelete}
            data-is-disable={arePermissionsStale}
          >
            <RemoveIcon sx={{ color: 'neutral900' }} />
          </StyledIconButton>
        )}

        {selectionCount === 1 && canEdit && (
          <StyledIconButton
            onClick={onEdit}
            data-is-disable={arePermissionsStale}
          >
            <EditOutlinedIcon sx={{ color: 'neutral900' }} />
          </StyledIconButton>
        )}
      </RowDirectionStack>

      <RowDirectionStack>
        <Typography variant="subtitle1" color="neutral900">
          {toLocale(selectionCount)}
        </Typography>

        <StyledCloseIconButton onClick={onBack || exitRoomFromSelectionMode}>
          <CloseIcon sx={{ color: 'neutral900' }} />
        </StyledCloseIconButton>
      </RowDirectionStack>
    </Container>
  )
})
